<template>
    <div
        :class="$style.sidebar"
        data-interface-section="sidebar"
        tabindex="0"
        @focusout="handleMouseLeave"
        @mouseleave="handleMouseLeave"
    >
        <Link
            :to="{ name: ROUTE_NAMES.MAIN }"
            data-interface-target="navigation-link"
            :class="[$style.icon_wrapper, $style.hovered]"
            color="rainbow"
        >
            <Icon
                name="fennek"
                :class="$style.icon"
            />
        </Link>
        <Link
            :to="{ name: ROUTE_NAMES.MAIN }"
            data-interface-target="navigation-link"
            :class="[$style.icon_wrapper, $style.non_hovered]"
            hoverAnimation="rainbow"
        >
            <Icon
                name="fennek"
                :class="$style.icon"
            />
        </Link>

        <div :class="$style.wrapper">
            <div
                ref="scrollerRef"
                :class="$style.scroller"
                @scroll="scrollStore.onVerticalScroll"
            >
                <template
                    v-for="item in preparedCategoriesList"
                    :key="item.id"
                >
                    <BaseLink
                        v-if="item.location"
                        :to="item.location"
                        data-interface-target="category"
                        :title="item.title"
                        :class="$style.item"
                    >
                        <!-- otherwise hidden (out of viewport) rows will be focusable -->
                        <div :class="$style.item_content">
                            <Icon
                                :name="item.id"
                                :alt="item.title"
                                :class="$style.item_icon"
                            />

                            <Typography
                                type="label"
                                size="m"
                                accent
                                :class="$style.item_content_title"
                                :responsive="false"
                            >
                                {{ item.title }}
                            </Typography>
                        </div>
                    </BaseLink>
                </template>
                <div :class="$style.rest">
                    <div :class="$style.sep_section" />
                    <template
                        v-for="item in pagesLinks"
                        :key="item.title"
                    >
                        <BaseLink
                            :title="item.title"
                            :class="$style.item"
                            tabindex="-1"
                            :to="item.to"
                            :externalTo="item.externalTo"
                            :target="item.target"
                            data-interface-target="navigation-link"
                        >
                            <div :class="$style.item_content">
                                <SeoOnly ariaHidden>
                                    {{ item.title }}
                                </SeoOnly>
                                <Icon
                                    :name="item.icon"
                                    :alt="item.title"
                                    :class="$style.item_icon"
                                />

                                <Typography
                                    type="label"
                                    size="m"
                                    accent
                                    :class="$style.item_content_title"
                                    :responsive="false"
                                >
                                    {{ item.title }}
                                </Typography>
                            </div>
                        </BaseLink>
                    </template>
                    <div :class="$style.sep_section" />
                    <div :class="$style.social_links">
                        <template
                            v-for="item in socialLinks"
                            :key="item.title"
                        >
                            <div
                                :title="item.title"
                                :class="$style.item"
                            >
                                <!-- otherwise hidden (out of viewport) rows will be focusable -->
                                <BaseLink
                                    :externalTo="item.externalTo"
                                    :target="item.target"
                                    data-interface-target="navigation-social-link"
                                    :class="$style.item_content"
                                >
                                    <Icon
                                        :name="item.icon"
                                        :alt="item.title"
                                        :class="$style.item_icon"
                                    />
                                    <SeoOnly ariaHidden>
                                        {{ item.title }}
                                    </SeoOnly>
                                </BaseLink>
                            </div>
                        </template>
                    </div>
                    <div :class="$style.sep_section" />
                    <div :class="$style.lang_section">
                        <LocaleSwitchersList :languageLinks="props.languageLinks" />
                    </div>
                    <div :class="$style.sep_section" />
                    <div :class="$style.copyright_section">
                        <Copyright />
                    </div>
                </div>
            </div>
            <div :class="[$style.shadow, $style.shadow_top, { [$style.visible]: topActive }]" />
            <div :class="[$style.shadow, $style.shadow_bottom, { [$style.visible]: bottomActive }]" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { type Ref, computed, ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import Icon, { type IconNameType } from '@/components/Icon/Icon.vue'
import Link from '@/components/Link.vue'
import { useScroll } from '@/composables/use-scroll'
import { useTr } from '@/composables/use-tr'
import type { AvailableLocale } from '@/i18n/helpers'
import { type ShortCategory, useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import {
    ABOUT_US_NAVIGATION_ITEM,
    DEVELOPERS_LANDPAGE_PATH,
    NAVIGATION_ALL_GAMES_LINKS,
    type NavLink,
    SOCIAL_LINKS,
} from '@/router/navigation-constants'

import Copyright from '../Copyright.vue'
import LocaleSwitchersList from '../LocaleSwitchers/LocaleSwitchersList.vue'
import SeoOnly from '../SeoOnly.vue'
import Typography from '../Typography.vue'

interface LeftSideProps {
    languageLinks?: Record<AvailableLocale, string>
}

const props = defineProps<LeftSideProps>()

const scrollStore = useScroll()
const { topActive, bottomActive } = scrollStore

const scrollerRef = ref<HTMLElement | null>(null)

const handleMouseLeave = (event: MouseEvent | FocusEvent) => {
    const currentTarget = event.currentTarget as Node
    const relatedTarget = event.relatedTarget as Node | null

    if (relatedTarget !== currentTarget && !currentTarget?.contains(relatedTarget)) {
        setTimeout(() => {
            if (scrollerRef.value) {
                // Reset scroll position after a short delay to allow for smooth scrolling
                scrollerRef.value.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }, 120)
    }
}

type Category =
    | ShortCategory
    | {
          id: IconNameType
          title: string
          location: RouteLocationRaw
      }

const tr = useTr()
const { mainData } = useGeneralData()
const menuCategoriesList = computed(() => mainData.value.menuCategoriesList || [])
const allGamesNavigation = computed(() => Object.values(NAVIGATION_ALL_GAMES_LINKS(tr)))

const preparedCategoriesList: Ref<Category[]> = computed(() => {
    return [
        { id: 'heart', location: { name: ROUTE_NAMES.PROFILE }, title: tr('myGames.title') },
        ...menuCategoriesList.value,
        ...allGamesNavigation.value.map((item) => ({
            id: item.icon,
            title: item.title,
            location: item.to,
        })),
    ]
})

const pagesLinks = computed<(NavLink & { icon: IconNameType })[]>(() => [
    {
        ...ABOUT_US_NAVIGATION_ITEM(tr),
        icon: 'fennek',
    },

    {
        title: tr('navigation.for_developers'),
        externalTo: DEVELOPERS_LANDPAGE_PATH,
        icon: 'bridge',
        target: '_blank',
    },
    {
        title: tr('navigation.contact_us'),
        icon: 'smile',
        to: { name: ROUTE_NAMES.CONTACTS },
    },
    {
        title: tr('navigation.terms_of_use'),
        icon: 'flag',
        to: { name: ROUTE_NAMES.TERMS_OF_USE },
    },
])

const socialLinks = computed<(NavLink & { icon: IconNameType })[]>(() => SOCIAL_LINKS(tr))
</script>

<style module>
:root {
    --item-height: 24px;
    --max-width: 236px;
    --hover-shift: 9px;
    --common-padding: 12px;
    --icon-spacing: calc(var(--hover-shift) + 3px);
    --transition-quick: 0.1s ease-out;
    --transition-normal: 0.13s;
    --icon-size: 32px;
    --icon-padding: 12px;
    --margin-top: 12px;
    --common-gap: 8px;
}

.sidebar {
    display: flex;
    flex-direction: column;
    height: 100vh;
    gap: var(--common-gap);
    border-radius: 20px;
    margin-top: var(--margin-top);
    touch-action: pan-y;
    overscroll-behavior-x: none;
}

.wrapper {
    position: relative;
    overflow-y: auto;
    transition: width var(--transition-normal);
    scrollbar-width: none;
    border-radius: 16px;
}

.scroller {
    box-sizing: border-box;
    overflow-y: auto;
    scrollbar-width: none;
    height: calc(
        100dvh - var(--margin-top) - var(--icon-padding) - var(--icon-size) - var(--icon-padding) - var(--common-gap)
    );
    width: 100%;
    border-radius: 16px;
    background-color: transparent;
    transition: background-color var(--transition-normal);
    scroll-snap-type: y mandatory;
    backdrop-filter: blur(20px);
    padding: 0 2px;
    margin-bottom: 18px;
    touch-action: pan-y;
    overscroll-behavior-x: none;
}

.sidebar:hover .scroller,
.sidebar:has(*:focus-visible) .scroller {
    background-color: rgba(29, 29, 33, 0.8);
}

.sidebar:hover .wrapper,
.sidebar:has(*:focus-visible) .wrapper {
    width: var(--max-width);
}

.shadow {
    position: absolute;
    left: 0;
    right: 0;
    height: 18px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.13s ease;
}

.shadow.visible {
    opacity: 1;
}

.shadow_top {
    top: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, #000 100%);
}

.shadow_bottom {
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 100%);
}

.sidebar:hover .shadow,
.sidebar:has(*:focus-visible) .shadow {
    opacity: 0;
}

.item {
    color: var(--purple-600);
    padding: 16px var(--icon-spacing) 0 var(--common-padding);
    display: flex;
    width: var(--max-width);
    box-sizing: border-box;
    will-change: transform;
    transition: transform var(--transition-quick);
    position: relative;
    align-items: center;
    cursor: pointer;
    scroll-snap-align: start;
    margin-left: 2px;
}

.item:before {
    content: '';
    position: absolute;
    inset: calc(var(--hover-shift) * -0.5);
    pointer-events: none;
}

.item_icon {
    --width-icon: 48px;
    --margin-icon: -12px;
    will-change: transform;
    transition: transform var(--transition-quick);
    width: var(--width-icon);
    height: var(--width-icon);
    transform: scale(0.5);
    flex-shrink: 0;
    margin: var(--margin-icon);
}

.item_content {
    outline: none;
    display: flex;
    align-items: center;
    gap: 12px;
}

/* Hover and focus states */
.item:hover,
.item:has(:focus-visible),
.item:focus-within {
    color: var(--electric-lime);
    outline: none;
}

.item:hover .item_icon,
.item:has(:focus-visible) .item_icon {
    transform: scale(0.7);
}

.item:has(+ .item:hover) .item_icon,
.item:hover + .item .item_icon,
.item:has(:focus-visible) + .item .item_icon,
.item:has(+ .item:has(:focus-visible)) .item_icon {
    transform: scale(0.6);
}

.item_content_title {
    color: var(--purple-600);
    text-align: left;
    transition: margin-left var(--transition-quick);
    margin-right: 15px;
    height: 1px;
    overflow: hidden;
}

.sidebar:hover .item .item_content_title,
.sidebar:has(*:focus-visible) .item .item_content_title {
    height: auto;
    overflow: visible;
}

.sidebar .item:hover .item_content_title,
.sidebar .item:has(:focus-visible) .item_content_title {
    color: var(--electric-lime);
    margin-left: 6px;
}

.icon_wrapper {
    align-self: flex-start;
    width: var(--icon-size);
    height: var(--icon-size);
    padding: var(--icon-padding);
    border-radius: 50%;
    background-color: var(--purple-400);
}

.icon_wrapper.hovered {
    display: none;
}

.icon {
    width: var(--icon-size);
    height: var(--icon-size);
}

.sidebar:hover .icon,
.sidebar:has(*:focus-visible) .icon,
.icon_wrapper:focus {
    color: black;
}

.sidebar:hover .icon_wrapper.hovered,
.sidebar:has(*:focus-visible) .icon_wrapper.hovered {
    display: flex;
}

.sidebar:hover .icon_wrapper.non_hovered,
.sidebar:has(*:focus-visible) .icon_wrapper.non_hovered {
    display: none;
}

/* Hidden sections */
.rest {
    width: var(--max-width);
    height: 1px;
    overflow: hidden;
}

.sidebar:hover .rest,
.sidebar:has(*:focus-visible) .rest {
    height: auto;
    overflow: visible;
}

.sep_section {
    background-color: var(--white-08);
    height: 1px;
    margin: var(--icon-spacing);
}

.lang_section,
.copyright_section {
    padding: 0 var(--icon-spacing);
    padding-left: var(--common-padding);
}

.copyright_section {
    padding-bottom: 23px;
}

/* Social links section */
.social_links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
}

.social_links .item {
    width: auto;
    flex-shrink: 0;
}

.social_links .item_icon {
    --width-icon: 64px;
    --margin-icon: -16px;
}
/* temp solution until Left side become part of header*/
:global(html:has(*[data-overlay-blur])) .wrapper {
    display: none;
}
</style>
